import React, { useState, useEffect } from 'react';
import './ViewDrawer.css';
import BodyLargeMedium from '../designSystem/BodyLargeMedium';
import closeIcon from "../../assets/images/closeIcon.svg";
import EventCard from './EventCard';
import BodySmallMedium from '../designSystem/BodySmallMedium';
import { useSelector, useDispatch } from 'react-redux';
import { getTaskLookup } from '../../actions/userActions/tasks/taskAction';

const ViewDrawer = ({ isOpen, onClose, day, events }) => {
    const [expandedVisitProfiles, setExpandedVisitProfiles] = useState({});
    const userDetail = useSelector((state) => state.employees_reducer.employee);
    const taskLookups = useSelector((state) => state.taskLookupReducer.taskLookups);
    const dispatch = useDispatch();
    const user = JSON.parse(localStorage.getItem("user"));
    const userId = user?.result[0]?.user.id;

    const formattedDate = day
        ? new Intl.DateTimeFormat('en-US', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        }).format(new Date(day.year, day.month - 1, day.day))
        : '';

    // Check if events has NEW visit profiles structure (visitProfiles array)
    const hasNewVisitProfilesStructure = () => {
        if (!events || !Array.isArray(events)) return false;
        return events.some(e => e.visitProfiles && e.visitProfiles.length > 0);
    };

    // Check if events has OLD visit profile structure (visitProfile object)
    const hasOldVisitProfileStructure = () => {
        if (!events || !Array.isArray(events)) return false;
        return events.some(e => e.visitProfile && e.visitProfile.visitProfileId);
    };

    const hasNewStructure = hasNewVisitProfilesStructure();
    const hasOldStructure = hasOldVisitProfileStructure();
    const hasAnyVisitProfiles = hasNewStructure || hasOldStructure;

    // Get all visit profiles with their names
const getVisitProfiles = () => {
    if (!events || !Array.isArray(events)) return [];
    
    const visitProfileIdsSet = new Set();
    
    // Collect visit profile IDs from BOTH structures
    events.forEach(event => {
        // NEW structure
        if (event.visitProfiles && event.visitProfiles.length > 0) {
            event.visitProfiles.forEach(vp => {
                visitProfileIdsSet.add(vp.visitProfileId);
            });
        }
        // OLD structure
        if (event.visitProfile && event.visitProfile.visitProfileId) {
            visitProfileIdsSet.add(event.visitProfile.visitProfileId);
        }
    });
    
    const visitProfileIds = Array.from(visitProfileIdsSet);
    
    // Try to match with userDetail to get proper names
    if (userDetail && userDetail !== "isLoading" && userDetail.visitProfiles) {
        const matchedProfiles = userDetail.visitProfiles
            .filter(profile => visitProfileIds.includes(profile.visitProfileId))
            .map(profile => ({
                visitProfileId: profile.visitProfileId,
                visitProfileName: profile.profileName
            }));
        
        if (matchedProfiles.length > 0) {
            return matchedProfiles;
        }
    }
    
    // Fallback: If no userDetail or no matches, use numbered names
    return visitProfileIds.map((id, index) => ({
        visitProfileId: id,
        visitProfileName: `Visit Profile ${index + 1}`
    }));
};

    const visitProfiles = getVisitProfiles();

    // Initialize all visit profiles as expanded when drawer opens
    useEffect(() => {
        if (isOpen && hasAnyVisitProfiles && visitProfiles.length > 0) {
            const initialExpandedState = {};
            visitProfiles.forEach(profile => {
                initialExpandedState[profile.visitProfileId] = true;
            });
            setExpandedVisitProfiles(initialExpandedState);
        } else {
            setExpandedVisitProfiles({});
        }
    }, [isOpen, events, hasAnyVisitProfiles, visitProfiles.length]);

    // Toggle visit profile expansion
    const toggleVisitProfile = (visitProfileId) => {
        setExpandedVisitProfiles(prev => ({
            ...prev,
            [visitProfileId]: !prev[visitProfileId]
        }));
    };

    // Get plan details for a specific visit profile
    const getPlanDetailsForProfile = (visitProfileId) => {
        if (!events || !Array.isArray(events)) return [];

        const planDetails = [];
        
        events.forEach(event => {
            // NEW structure - check visitProfiles array
            if (event.visitProfiles && event.visitProfiles.length > 0) {
                const matchingProfile = event.visitProfiles.find(
                    vp => vp.visitProfileId === visitProfileId
                );
                
                if (matchingProfile && matchingProfile.planDetails) {
                    planDetails.push(...matchingProfile.planDetails);
                }
            }
            
            // OLD structure - check visitProfile object and planDetails
            if (event.visitProfile && 
                event.visitProfile.visitProfileId === visitProfileId &&
                event.planDetails && 
                event.planDetails.length > 0) {
                planDetails.push(...event.planDetails);
            }
        });
        
        return planDetails;
    };

    // Get direct plan details (when no visit profiles)
    const getDirectPlanDetails = () => {
        if (!events || !Array.isArray(events)) return [];

        const planDetails = [];
        events.forEach(event => {
            if (event.planDetails && event.planDetails.length > 0) {
                planDetails.push(...event.planDetails);
            }
        });
        
        return planDetails;
    };

    // Check if on leave
    const isOnLeave = events && Array.isArray(events) && events.some(e => e.onLeave);

    // Fetch task lookups when drawer opens
    useEffect(() => {
        if (isOpen && userId) {
            dispatch(getTaskLookup(userId));
        }
    }, [isOpen, userId, dispatch]);

    return (
        <>
            <div
                className={`view-drawer-backdrop ${isOpen ? 'open' : ''}`}
                onClick={onClose}
            ></div>

            <div className={`view-drawer-container ${isOpen ? 'open' : ''}`}>
                <div className="view-drawer-header">
                    <div className="header-content">
                        <BodyLargeMedium text={formattedDate} color='#384153' />
                        {isOnLeave && (
                            <span className="on-leave-badge">On Leave</span>
                        )}
                    </div>
                    <button className='close-icon' onClick={onClose}>
                        <img src={closeIcon} alt="close" />
                    </button>
                </div>

                <div className='view-drawer-main'>
                    {/* Visit Profile Collapsible Sections */}
                    {hasAnyVisitProfiles && visitProfiles.length > 0 ? (
                        visitProfiles.map((profile) => {
                            const profilePlanDetails = getPlanDetailsForProfile(profile.visitProfileId);
                            const isExpanded = expandedVisitProfiles[profile.visitProfileId];
                            
                            return (
                                <div key={profile.visitProfileId} className="visit-profile-section">
                                    <button 
                                        className="visit-profile-header"
                                        onClick={() => toggleVisitProfile(profile.visitProfileId)}
                                    >
                                        <div className="visit-profile-title">
                                            <svg 
                                                className={`chevron-icon ${isExpanded ? 'expanded' : ''}`}
                                                width="20" 
                                                height="20" 
                                                viewBox="0 0 20 20" 
                                                fill="none"
                                            >
                                                <path 
                                                    d="M7.5 5L12.5 10L7.5 15" 
                                                    stroke="#384153" 
                                                    strokeWidth="1.5" 
                                                    strokeLinecap="round" 
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                            <BodySmallMedium text={profile.visitProfileName} color='#1E293B' />
                                        </div>
                                    </button>
                                    
                                    {isExpanded && (
                                        <div className="visit-profile-content">
                                            {profilePlanDetails && profilePlanDetails.length > 0 ? (
                                                profilePlanDetails.map((plan, index) => (
                                                    <EventCard 
                                                        key={plan.id || index} 
                                                        event={plan} 
                                                        planCityName={plan.cityName}
                                                        taskLookups={taskLookups} 
                                                    />
                                                ))
                                            ) : (
                                                <div className="empty-state-small">
                                                    <p>No plans for this visit profile</p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    ) : (
                        /* No Visit Profiles - Show Direct Plan Details */
                        isOpen && getDirectPlanDetails().length > 0 ? (
                            getDirectPlanDetails().map((plan, index) => (
                                <EventCard 
                                    key={plan.id || index} 
                                    event={plan} 
                                    planCityName={plan.cityName}
                                    taskLookups={taskLookups} 
                                />
                            ))
                        ) : (
                            !isOnLeave && (
                                <div className="empty-state">
                                    <p>No plans available for this day</p>
                                </div>
                            )
                        )
                    )}
                </div>
            </div>
        </>
    );
};

export default ViewDrawer;